import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, Radar, AreaChart, Area
} from 'recharts';
import { useAuth } from '../context/AuthContext';

const DIM_COLORS = {
  strategy:'#06b6d4', decision:'#7c3aed', process:'#f59e0b',
  accountability:'#f97316', execution:'#22c55e', financial:'#10b981',
  hr_culture:'#ec4899', sales_marketing:'#f43f5e', operations:'#0ea5e9',
  technology:'#8b5cf6', risk:'#dc2626', customer_exp:'#d946ef'
};

const ScoreColor = s => s<=2?'#ef4444':s<=3?'#f97316':s<=3.8?'#eab308':s<=4.5?'#22c55e':'#06b6d4';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#0d1220', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '10px 14px', fontSize: 12 }}>
      <div style={{ fontWeight: 600, marginBottom: 6, color: '#e8edf5' }}>{label}</div>
      {payload.map(p => (
        <div key={p.name} style={{ color: p.color, marginBottom: 3 }}>
          {p.name}: <strong>{p.value}</strong>
        </div>
      ))}
    </div>
  );
};

export default function TrendDashboard({ showToast }) {
  const navigate = useNavigate();
  const location = useLocation();
  const pageData = location.state || {};
  const { authFetch, user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [selCompany, setSelCompany] = useState(pageData?.companyId || '');
  const [rounds, setRounds] = useState([]);
  const [trendData, setTrendData] = useState([]);
  const [dimTrends, setDimTrends] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeDims, setActiveDims] = useState([]);

  useEffect(() => {
    authFetch('/api/companies').then(r => r.json()).then(d => setCompanies(Array.isArray(d) ? d : []));
  }, [authFetch]);

  useEffect(() => {
    if (!selCompany) return;
    loadTrend();
  }, [selCompany]);

  const loadTrend = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`/api/reports/${selCompany}/trend`);
      const data = await res.json();
      if (res.ok) {
        setRounds(data.rounds || []);
        setTrendData(data.trend || []);
        setDimTrends(data.dim_trend || []);
        setActiveDims(data.active_dims || []);
      } else {
        showToast(data.error || 'Could not load trend data', 'error');
      }
    } catch (e) { showToast('Connection error', 'error'); }
    setLoading(false);
  };

  const currentCompany = companies.find(c => c.id === selCompany);
  const latestScore = trendData[trendData.length - 1]?.org_score;
  const firstScore = trendData[0]?.org_score;
  const totalDelta = trendData.length > 1 ? Math.round((latestScore - firstScore) * 10) / 10 : null;

  // Radar for latest round
  const latestRadarData = activeDims.map(d => ({
    subject: d.code,
    score: trendData[trendData.length - 1]?.[d.key] || 0,
    prev: trendData.length > 1 ? trendData[trendData.length - 2]?.[d.key] || 0 : null
  }));

  if (user?.plan === 'free') return (
    <div>
      <div className="page-header">
        <div className="page-title">Trend Dashboard</div>
        <div className="page-sub">Track organizational improvement across audit rounds over time</div>
      </div>
      <div className="card" style={{ textAlign: 'center', padding: 56, borderColor: 'var(--blue-border)' }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>📈</div>
        <div style={{ fontFamily: 'var(--ff-d)', fontSize: 20, fontWeight: 800, marginBottom: 8 }}>Trend Tracking is a Pro Feature</div>
        <div style={{ color: 'var(--text2)', fontSize: 13, marginBottom: 24, maxWidth: 400, margin: '0 auto 24px' }}>
          Run multiple audit rounds and track how your organization improves over time — score trends, dimension changes, and improvement velocity.
        </div>
        <button className="btn btn-primary" style={{ padding: '11px 28px' }} onClick={() => navigate('/billing')}>Upgrade to Pro →</button>
      </div>
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Trend Dashboard 📈</div>
        <div className="page-sub">Track organizational improvement across audit rounds over time</div>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <label className="form-label">Company</label>
            <select className="form-select" value={selCompany} onChange={e => setSelCompany(e.target.value)}>
              <option value="">Select company...</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          {selCompany && (
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/respondents', { state: { companyId: selCompany } })}>
              + New Round
            </button>
          )}
        </div>
      </div>

      {loading && <div style={{ textAlign: 'center', padding: 60 }}><div className="loader" style={{ margin: '0 auto' }}></div></div>}

      {!loading && selCompany && trendData.length === 0 && (
        <div className="card empty-state">
          <div className="empty-icon">📊</div>
          <div className="empty-title">No trend data yet</div>
          <div className="empty-sub">You need at least 2 completed audit rounds to see trends. Run your first assessment, then create a new round and run it again after 90 days.</div>
          <button className="btn btn-primary" onClick={() => navigate('/respondents', { state: { companyId: selCompany } })}>Manage Rounds →</button>
        </div>
      )}

      {!loading && trendData.length > 0 && (
        <div className="fade-in">
          {/* KPI row */}
          <div className="g4" style={{ marginBottom: 24 }}>
            <div className="card" style={{ gridColumn: 'span 2', background: 'linear-gradient(135deg,rgba(0,212,255,0.05),rgba(124,58,237,0.05))', borderColor: 'var(--blue-border)' }}>
              <div className="card-title">{currentCompany?.name}</div>
              <div style={{ display: 'flex', gap: 28, alignItems: 'center', flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontFamily: 'var(--ff-m)', fontSize: 48, fontWeight: 500, color: ScoreColor(latestScore), lineHeight: 1 }}>{latestScore}</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3, fontFamily: 'var(--ff-m)' }}>LATEST SCORE / 5</div>
                </div>
                {totalDelta !== null && (
                  <div>
                    <div style={{ fontFamily: 'var(--ff-m)', fontSize: 32, fontWeight: 500, color: totalDelta > 0 ? 'var(--green)' : totalDelta < 0 ? 'var(--red)' : 'var(--text3)', lineHeight: 1 }}>
                      {totalDelta > 0 ? '+' : ''}{totalDelta}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>
                      {totalDelta > 0 ? '↑ Improved' : totalDelta < 0 ? '↓ Declined' : '→ No change'} since Round 1
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="card">
              <div className="card-title">Audit Rounds</div>
              <div style={{ fontFamily: 'var(--ff-m)', fontSize: 32, fontWeight: 500, color: 'var(--gold-2)' }}>{rounds.length}</div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>Completed rounds</div>
            </div>
            <div className="card">
              <div className="card-title">Velocity</div>
              <div style={{ fontFamily: 'var(--ff-m)', fontSize: 32, fontWeight: 500, color: totalDelta > 0.5 ? 'var(--green)' : totalDelta < 0 ? 'var(--red)' : 'var(--yellow)' }}>
                {trendData.length < 2 ? '—' : totalDelta > 0.5 ? 'Fast' : totalDelta > 0 ? 'Slow' : totalDelta === 0 ? 'Flat' : 'Declining'}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>Improvement rate</div>
            </div>
          </div>

          {/* Overall score trend chart */}
          <div className="sec-header"><div className="sec-title">Overall Score Trend</div><div className="sec-line"></div></div>
          <div className="card" style={{ marginBottom: 24 }}>
            <div style={{ height: 220 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trendData} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
                  <defs>
                    <linearGradient id="scoreGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#00d4ff" stopOpacity={0.2}/>
                      <stop offset="95%" stopColor="#00d4ff" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--bg-elevated)" />
                  <XAxis dataKey="label" tick={{ fill: 'var(--text2)', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis domain={[1, 5]} tick={{ fill: 'var(--text3)', fontSize: 10 }} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="org_score" name="Org Score" stroke="#00d4ff" strokeWidth={2.5} fill="url(#scoreGrad)" dot={{ fill: '#00d4ff', r: 5 }} activeDot={{ r: 7 }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div style={{ display: 'flex', gap: 12, marginTop: 14, flexWrap: 'wrap' }}>
              {trendData.map((r, i) => (
                <div key={i} style={{ padding: '6px 12px', background: 'var(--bg3)', borderRadius: 8, border: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--ff-m)', marginBottom: 2 }}>{r.label}</div>
                  <div style={{ fontFamily: 'var(--ff-m)', fontSize: 16, color: ScoreColor(r.org_score), fontWeight: 600 }}>{r.org_score}</div>
                  <div style={{ fontSize: 10, color: 'var(--text3)' }}>{r.respondents} respondents</div>
                </div>
              ))}
            </div>
          </div>

          {/* Dimension trend lines */}
          {activeDims.length > 0 && trendData.length > 1 && (
            <>
              <div className="sec-header"><div className="sec-title">Dimension Trends</div><div className="sec-line"></div></div>
              <div className="card" style={{ marginBottom: 24 }}>
                <div style={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trendData} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--bg-elevated)" />
                      <XAxis dataKey="label" tick={{ fill: 'var(--text2)', fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis domain={[1, 5]} tick={{ fill: 'var(--text3)', fontSize: 10 }} axisLine={false} tickLine={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                      {activeDims.slice(0, 6).map(d => (
                        <Line key={d.key} type="monotone" dataKey={d.key} name={d.label}
                          stroke={DIM_COLORS[d.key] || '#8a96a8'} strokeWidth={2} dot={{ r: 4 }}
                          activeDot={{ r: 6 }} connectNulls />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </>
          )}

          {/* Per-dimension deltas table */}
          {dimTrends.length > 0 && (
            <>
              <div className="sec-header"><div className="sec-title">Dimension Changes (First → Latest)</div><div className="sec-line"></div></div>
              <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 24 }}>
                <table className="table">
                  <thead><tr><th>Dimension</th><th>First Round</th><th>Latest Round</th><th>Change</th><th>Trend</th></tr></thead>
                  <tbody>
                    {dimTrends.map(d => (
                      <tr key={d.key}>
                        <td style={{ fontWeight: 600, color: 'var(--text)', fontSize: 13 }}>{d.label}</td>
                        <td style={{ fontFamily: 'var(--ff-m)', fontSize: 12, color: ScoreColor(d.first) }}>{d.first || '—'}</td>
                        <td style={{ fontFamily: 'var(--ff-m)', fontSize: 12, color: ScoreColor(d.latest) }}>{d.latest || '—'}</td>
                        <td>
                          <span className={d.delta > 0 ? 'delta-positive' : d.delta < 0 ? 'delta-negative' : 'delta-neutral'} style={{ fontSize: 13, fontWeight: 600 }}>
                            {d.delta > 0 ? '+' : ''}{d.delta || '—'}
                          </span>
                        </td>
                        <td>
                          <span className={`tag ${d.delta > 0.3 ? 'priority-strong' : d.delta > 0 ? 'priority-healthy' : d.delta < -0.3 ? 'priority-critical' : d.delta < 0 ? 'priority-weak' : ''}`} style={{ fontSize: 10 }}>
                            {d.delta > 0.3 ? '↑ Strong Gain' : d.delta > 0 ? '↑ Improving' : d.delta < -0.3 ? '↓ Declining' : d.delta < 0 ? '↓ Slight Drop' : '→ Flat'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Latest vs previous radar */}
          {latestRadarData.length > 0 && latestRadarData.some(d => d.prev !== null) && (
            <>
              <div className="sec-header"><div className="sec-title">Latest vs Previous Round Radar</div><div className="sec-line"></div></div>
              <div className="card" style={{ marginBottom: 24 }}>
                <div style={{ height: 280 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={latestRadarData} margin={{ top: 10, right: 30, bottom: 10, left: 30 }}>
                      <PolarGrid stroke="rgba(255,255,255,0.07)" />
                      <PolarAngleAxis dataKey="subject" tick={{ fill: 'var(--text2)', fontSize: 10, fontFamily: 'monospace' }} />
                      <Radar name="Latest" dataKey="score" stroke="#00d4ff" fill="#00d4ff" fillOpacity={0.15} strokeWidth={2} />
                      {latestRadarData.some(d => d.prev !== null) && (
                        <Radar name="Previous" dataKey="prev" stroke="#7c3aed" fill="#7c3aed" fillOpacity={0.08} strokeWidth={1.5} strokeDasharray="4 2" />
                      )}
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Tooltip contentStyle={{ background: '#0d1220', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, fontSize: 12 }} />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </>
          )}

          <div className="card" style={{ textAlign: 'center', padding: 32, borderColor: 'rgba(0,212,255,0.15)' }}>
            <div style={{ fontFamily: 'var(--ff-d)', fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Ready to measure your next progress?</div>
            <div style={{ color: 'var(--text2)', fontSize: 13, marginBottom: 16 }}>Create a new audit round, share the link, and track exactly what improved.</div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button className="btn btn-primary" onClick={() => navigate('/respondents', { state: { companyId: selCompany } })}>Start New Round →</button>
              <button className="btn btn-ghost" onClick={() => navigate('/report', { state: { companyId: selCompany } })}>View Full Report</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
